import type { 
  LoginCredentials, 
  RegisterData, 
  LoginResponse, 
  RegisterResponse, 
  RefreshTokenResponse,
  User 
} from '../types/auth';
import { authApi, publicApi } from './apiClient';
import { createSecureLoginPayload } from '../utils/security';

export const authService = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      // Create secure login payload with encryption if supported
      const securePayload = await createSecureLoginPayload(
        credentials.username,
        credentials.password
      );
      
      const response = await publicApi.post('/auth/login', securePayload);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.mensaje || 'Login failed');
    }
  },

  async register(data: RegisterData): Promise<RegisterResponse> {
    try {
      const response = await publicApi.post('/auth/register', {
        username: data.username,
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
        preferred_language: data.preferred_language,
        club_id: data.club_id,
        role_id: data.role_id
      });
      
      // Handle both new standardized format and legacy format
      if (response.data.success && response.data.data) {
        // New format: {success: true, message: "...", data: {usuario: ...}}
        return {
          mensaje: response.data.message,
          usuario: response.data.data.usuario
        };
      } else {
        // Legacy format: {mensaje: "...", usuario: ...}
        return response.data;
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.mensaje || 'Registration failed');
    }
  },

  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    try {
      const response = await publicApi.post('/auth/refresh', {}, {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      });
      // Transform backend response to match frontend expectations
      const data = response.data;
      return {
        token_acceso: data.token_acceso,
        token_refresco: data.token_refresco || refreshToken
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.mensaje || 'Token refresh failed');
    }
  },

  async getCurrentUser(): Promise<User> {
    try {
      const response = await authApi.get('/auth/me');
      return response.data.usuario;
    } catch (error: any) {
      throw new Error(error.response?.data?.mensaje || 'Failed to get user info');
    }
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await authApi.post('/auth/cambiar-contraseña', {
        current_password: currentPassword,
        new_password: newPassword,
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.mensaje || 'Failed to change password');
    }
  },

  logout(): void {
    // Clear local storage
    localStorage.removeItem('auth-storage');
    
    // Optional: Call logout endpoint to invalidate token on server
    authApi.post('/auth/logout').catch(() => {
      // Ignore errors on logout
    });
  },
};